import React, { useState } from "react";
import { useGetRoutingParametersQuery, useUpdateRoutingParameterMutation } from "../../features/itemRoutings/itemRoutingApi";
import { assertSuccessfulResponse, getRequestErrorMessage } from "../../services/apiMessages";
import ActionButton from "../../components/ui/ActionButton";
import ParameterDialog from "./ParameterDialog";

const definitionFields = ["parameter_name", "unit", "direct_parameter", "lower_control_limit", "upper_control_limit", "lower_specification_limit", "upper_specification_limit"];
const sameDefinition = (first, second) => definitionFields.every((field) => (first[field] ?? null) === (second[field] ?? null));

export default function OperationParameters({ plantId, itemId, operationId, drafts = [], onRemove, disabled }) {
  const [editing, setEditing] = useState(null);
  const [updateParameter] = useUpdateRoutingParameterMutation();
  const { currentData, isFetching, error, refetch } = useGetRoutingParametersQuery({
    plant_id: plantId, item_id: itemId, operation_id: operationId,
  }, { skip: !plantId || !itemId || !operationId, refetchOnMountOrArgChange: true });
  if (!operationId) return null;
  const saved = currentData || [];
  const parameters = [
    ...saved.map((parameter) => ({ ...parameter, key: parameter.id || parameter.parameter_id, saved: true })),
    ...drafts.filter((draft) => !draft.saved || !saved.some((parameter) => sameDefinition(parameter, draft))),
  ];
  return (
    <section aria-label={`Parameters for ${operationId}`} className="space-y-2 rounded-md bg-slate-50 p-3">
      <h3 className="text-sm font-semibold text-slate-800">Parameters ({parameters.length})</h3>
      {isFetching && <p className="text-sm text-slate-500">Loading saved parameters...</p>}
      {error && <div role="alert" className="text-sm text-red-600">
        {getRequestErrorMessage(error, "Unable to load saved parameters.")}
        <ActionButton label="Retry loading parameters" onClick={refetch} disabled={disabled || isFetching} />
      </div>}
      {!isFetching && !error && !parameters.length && <p className="text-sm text-slate-500">No parameters added for this operation.</p>}
      <ul className="space-y-2">
        {parameters.map((parameter) => <li key={parameter.key} className="rounded border border-slate-200 bg-white p-3 text-sm text-slate-700">
          <div className="flex items-center justify-between gap-2">
            <span>{parameter.parameter_name}{parameter.unit ? ` (${parameter.unit})` : ""} · {parameter.saved ? "Saved" : "Pending save"}</span>
            {parameter.saved && (parameter.id || parameter._id || parameter.uid) && <ActionButton action="update" size="sm" label={`Update parameter ${parameter.parameter_name}`} disabled={disabled} onClick={() => setEditing(parameter)} />}
            {!parameter.saved && <ActionButton action="delete" iconOnly size="sm" label={`Remove parameter ${parameter.parameter_name}`} disabled={disabled} onClick={() => onRemove(parameter.key)} />}
          </div>
          <p className="mt-1 text-xs text-slate-500">{parameter.direct_parameter === false ? "Indirect parameter" : "Direct parameter"} · Unit: {parameter.unit || "No unit"}</p>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {[["lower_control_limit", "Lower Control Limit"], ["upper_control_limit", "Upper Control Limit"], ["lower_specification_limit", "Lower Specification Limit"], ["upper_specification_limit", "Upper Specification Limit"]].map(([field, label]) => (
              <div key={field}><dt className="text-slate-500">{label}</dt><dd>{parameter[field] ?? "—"}</dd></div>
            ))}
          </dl>
        </li>)}
      </ul>
      {editing && <ParameterDialog
        key={editing.key}
        initialValues={editing}
        operationLabel={operationId}
        onClose={() => setEditing(null)}
        onAdd={async (values) => {
          assertSuccessfulResponse(await updateParameter({ uid: editing.id || editing._id || editing.uid, ...values }).unwrap(), "Unable to update parameter.");
          setEditing(null);
        }}
      />}
    </section>
  );
}
